import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from 'antd';
import Button from 'components/generalUI/Button';
import OpenDayPicker from 'components/sharedComponents/OpenDayPicker';
import priceFormat from 'utils/formats/priceFormat';
import TicketCounter from 'components/TicketInformation/TicketCounter';
import ActivityTimeSelector from 'components/TicketInformation/ActivityTimeSelector';
import CalendarLegend from 'components/TicketInformation/CalendarLegend';
import { TicketInformationViewModel } from 'views/TicketInformation/ticketInformation.viewModel';
import styles from './TicketConfigurator.module.scss';

type TicketConfiguratorProps = Pick<
  TicketInformationViewModel,
  | 'adultTickets'
  | 'otherTickets'
  | 'setAdultTickets'
  | 'setOtherTickets'
  | 'adultValue'
  | 'otherValue'
  | 'canAddMoreTickets'
  | 'ticketCount'
  | 'selectedDate'
  | 'onSelectDate'
  | 'isDateLocked'
  | 'unavailableDates'
  | 'soldOutDates'
  | 'onMonthChange'
  | 'timedActivities'
  | 'selectedTimes'
  | 'onSelectTime'
  | 'total'
  | 'canContinue'
  | 'isRemoval'
  | 'onContinue'
>;

const TicketConfigurator: FC<TicketConfiguratorProps> = (
  props: TicketConfiguratorProps
) => {
  const { t } = useTranslation();
  const {
    adultTickets,
    otherTickets,
    setAdultTickets,
    setOtherTickets,
    adultValue,
    otherValue,
    canAddMoreTickets,
    ticketCount,
    selectedDate,
    onSelectDate,
    isDateLocked,
    unavailableDates,
    soldOutDates,
    onMonthChange,
    timedActivities,
    selectedTimes,
    onSelectTime,
    total,
    canContinue,
    isRemoval,
    onContinue,
  } = props;

  const today = new Date().toISOString().split('T')[0];

  const counterSection = useRef<HTMLDivElement>(null);

  const scrollToCounter = () =>
    counterSection.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  return (
    <div className={styles.Card}>
      <div className={styles.CounterSection} ref={counterSection}>
        <h3 className={styles.StepTitle}>{t('pages.ticketInformation.stepTickets')}</h3>
        <TicketCounter
          adultTickets={adultTickets}
          otherTickets={otherTickets}
          setAdultTickets={setAdultTickets}
          setOtherTickets={setOtherTickets}
          adultValue={adultValue}
          otherValue={otherValue}
          canAddMoreTickets={canAddMoreTickets}
        />
      </div>

      <Divider className={styles.Divider} />

      <div className={styles.CalendarSection}>
        <h3 className={styles.StepTitle}>{t('pages.ticketInformation.stepDate')}</h3>
        {isDateLocked && (
          <p className={styles.LockedDateHint}>
            {t('pages.ticketInformation.dateLockedHint')}
          </p>
        )}
        <OpenDayPicker
          startDate={today}
          selectedDate={selectedDate}
          onSelectDate={onSelectDate}
          unavailableDates={unavailableDates}
          soldOutDates={soldOutDates}
          onMonthChange={onMonthChange}
        />
        <CalendarLegend />
      </div>

      {selectedDate && timedActivities.length > 0 && (
        <>
          <Divider className={styles.Divider} />
          <div className={styles.TimesSection}>
            <h3 className={styles.StepTitle}>{t('pages.ticketInformation.stepTime')}</h3>
            {ticketCount === 0 ? (
              <div className={styles.TimesPrompt}>
                <p className={styles.TimesPromptText}>
                  {t('pages.ticketInformation.ticketsFirstPrompt')}
                </p>
                <Button kind="secondary" onClick={scrollToCounter}>
                  {t('pages.ticketInformation.chooseTicketsCta')}
                </Button>
              </div>
            ) : (
              timedActivities.map((activity) => (
                <ActivityTimeSelector
                  key={activity.id}
                  activity={activity}
                  ticketCount={ticketCount}
                  selectedTimeId={selectedTimes[activity.id]}
                  onSelect={onSelectTime}
                />
              ))
            )}
          </div>
        </>
      )}

      <Divider className={styles.Divider} />

      <div className={styles.TotalSection}>
        <div className={styles.TotalRow}>
          <p className={styles.TotalLabel}>{t('pages.ticketInformation.total')}</p>
          <h2 className={styles.TotalValue}>{priceFormat.format(total)}</h2>
        </div>
        <Button
          className={styles.ContinueButton}
          size="large"
          disabled={!canContinue}
          onClick={onContinue}
        >
          {isRemoval
            ? t('pages.ticketInformation.removeFromCart')
            : t('pages.ticketInformation.continue')}
        </Button>
      </div>
    </div>
  );
};

export default TicketConfigurator;
